/// <reference types="vite/client" />
import { useCallback, useEffect, useState } from "react";
import { ErrorBanner } from "../components/ErrorBanner";

// ─── Wire format ──────────────────────────────────────────────────────

interface DraftRow {
  id: string;
  social_account: {
    id: string;
    display_name: string;
    type: string;
    auto_publish: number;
    brand_voice?: { tone?: string; pronouns?: string } | null;
  };
  status: string;
  content: string;
  trend_source: string | null;
  citations: string[] | null;
  verdict: string | null;
  verifier_feedback: string[] | null;
  unipile_post_id: string | null;
  metrics: { likes?: number; comments?: number; reach?: number } | null;
  created_at: number;
  updated_at: number;
  published_at: number | null;
}

interface AccountLite {
  id: string;
  display_name: string;
  auto_publish: boolean;
}

const STATUS_COLOR: Record<string, string> = {
  draft: "var(--color-accent)",
  approved: "var(--color-status-running)",
  published: "var(--color-status-active)",
  rejected: "var(--color-text-dim)",
  failed: "var(--color-status-error)",
};

// Verifier verdict → theme token (mirrors the V1 review page palette).
const VERDICT_COLOR: Record<string, string> = {
  PASS: "var(--color-status-active)",
  CONDITIONAL: "var(--color-status-running)",
  FAIL: "var(--color-status-error)",
  BLOCK: "var(--color-status-error)",
};

function fmtTime(unix: number | null): string {
  if (!unix) return "—";
  const d = new Date(unix * 1000);
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
}

/**
 * /posts — unified V2 LinkedIn draft queue (#20). The page reads
 * `posts_published` via /api/posts/drafts; account configuration (brand
 * voice, frequency, topic mix) lives on /linkedin (LinkedIn Hub). The
 * per-account Auto / Draft+review mode switch is surfaced here too (#21)
 * so reviewers can flip publishing posture next to the queue.
 */
export function Posts() {
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [accounts, setAccounts] = useState<AccountLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filterAccount, setFilterAccount] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [draftsRes, accountsRes] = await Promise.all([
        fetch("/api/posts/drafts"),
        fetch("/api/social-accounts"),
      ]);
      if (!draftsRes.ok) throw new Error(`drafts HTTP ${draftsRes.status}`);
      const { drafts: d } = (await draftsRes.json()) as { drafts: DraftRow[] };
      setDrafts(d ?? []);
      if (accountsRes.ok) {
        const { accounts: a } = (await accountsRes.json()) as { accounts: AccountLite[] };
        setAccounts(a ?? []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const patchDraft = useCallback(async (id: string, body: { content?: string; status?: string }) => {
    setBusyId(id);
    try {
      const res = await fetch(`/api/posts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }, [loadAll]);

  const publishDraft = useCallback(async (id: string) => {
    setBusyId(id);
    try {
      const res = await fetch(`/api/posts/${id}/publish`, { method: "POST" });
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; status?: string };
      if (!body.ok) throw new Error(body.status ?? `HTTP ${res.status}`);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }, [loadAll]);

  const patchAccount = useCallback(async (id: string, body: Record<string, unknown>) => {
    try {
      const res = await fetch(`/api/social-accounts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [loadAll]);

  if (error && drafts.length === 0) {
    return <ErrorBanner message={error} onRetry={loadAll} context="posts" />;
  }

  const filteredDrafts = drafts.filter(
    (d) =>
      (filterAccount === "all" || d.social_account.id === filterAccount) &&
      (filterStatus === "all" || d.status === filterStatus),
  );
  const draftCount = drafts.filter((d) => d.status === "draft").length;

  return (
    <div className="space-y-8 lg:space-y-10">
      <header className="reveal">
        <p className="eyebrow">Console · 08</p>
        <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
          <span className="text-[var(--color-text)]">Posts </span>
          <span className="text-accent-gradient">Queue</span>
        </h2>
        <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
          File unifiée des drafts LinkedIn (Marketing V2, grounded Perplexity + verifier). {draftCount} draft{draftCount > 1 ? "s" : ""} en attente. Config des comptes → LinkedIn Hub.
        </p>
      </header>

      {/* Per-account publishing mode (#21) */}
      {accounts.length > 0 && (
        <section className="space-y-3">
          <div className="section-rule">
            <p className="eyebrow">// Mode de publication</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {accounts.map((a) => (
              <ModeSwitch key={a.id} account={a} onToggle={() => patchAccount(a.id, { auto_publish: !a.auto_publish })} />
            ))}
          </div>
        </section>
      )}

      {/* Drafts queue */}
      <section className="space-y-4">
        <div className="section-rule flex items-center justify-between gap-2 flex-wrap">
          <p className="eyebrow">// Queue</p>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="font-mono text-xs px-2 py-1 rounded"
              style={{ background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
            >
              <option value="all">Tous statuts</option>
              {["draft", "approved", "published", "rejected", "failed"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              value={filterAccount}
              onChange={(e) => setFilterAccount(e.target.value)}
              className="font-mono text-xs px-2 py-1 rounded"
              style={{ background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
            >
              <option value="all">Tous les comptes</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.display_name}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && filteredDrafts.length === 0 && (
          <p className="font-mono text-xs text-[var(--color-text-dim)] blink">Loading…</p>
        )}
        {!loading && filteredDrafts.length === 0 && (
          <div className="panel p-8 sm:p-12 text-center">
            <p className="eyebrow">Queue vide</p>
            <p className="mt-2 font-mono text-[11px] tracking-wider uppercase text-[var(--color-text-dim)]">
              Marketing génère un draft à chaque cron tick (4h) si quota non atteint.
            </p>
          </div>
        )}

        <div className="space-y-3">
          {filteredDrafts.map((d) => (
            <DraftCard
              key={d.id}
              draft={d}
              busy={busyId === d.id}
              onPatch={(body) => patchDraft(d.id, body)}
              onPublish={() => publishDraft(d.id)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function ModeSwitch({ account, onToggle }: { account: AccountLite; onToggle: () => void }) {
  const auto = account.auto_publish;
  return (
    <button
      onClick={onToggle}
      className="panel px-3 py-2 flex items-center gap-2 transition-colors"
      style={{ borderColor: auto ? "var(--color-accent)" : "var(--color-border)" }}
      title="Bascule Auto ↔ Draft + review"
    >
      <span className="font-display italic text-sm text-[var(--color-text)]">{account.display_name}</span>
      <span
        className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
        style={{
          background: auto ? "var(--color-accent-soft)" : "var(--color-bg-card)",
          color: auto ? "var(--color-accent-glow)" : "var(--color-text-muted)",
          border: `1px solid ${auto ? "var(--color-accent)" : "var(--color-border)"}`,
        }}
      >
        {auto ? "Auto" : "Draft + review"}
      </span>
    </button>
  );
}

function DraftCard({
  draft,
  busy,
  onPatch,
  onPublish,
}: {
  draft: DraftRow;
  busy: boolean;
  onPatch: (body: { content?: string; status?: string }) => void;
  onPublish: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(draft.content);
  const canPublish = (draft.status === "draft" || draft.status === "approved" || draft.status === "failed") && !busy;
  const autoMode = draft.social_account.auto_publish === 1;

  // LinkedIn-like preview: split trailing hashtags from the body.
  const tokens = draft.content.trim().split(/\s+/);
  let splitAt = tokens.length;
  while (splitAt > 0 && tokens[splitAt - 1].startsWith("#")) splitAt--;
  const body = tokens.slice(0, splitAt).join(" ");
  const hashtags = tokens.slice(splitAt);
  const voice = draft.social_account.brand_voice;
  const showFeedback = draft.verdict && draft.verdict !== "PASS" && draft.verifier_feedback && draft.verifier_feedback.length > 0;

  const handlePublish = () => {
    if (autoMode && !window.confirm("Publier maintenant sur LinkedIn (compte en mode Auto) ?")) return;
    onPublish();
  };

  return (
    <div className="panel p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-display italic text-base text-[var(--color-text)]">{draft.social_account.display_name}</span>
        <span className="pill" style={{ color: STATUS_COLOR[draft.status] ?? "var(--color-text-muted)", borderColor: "var(--color-border)", background: "var(--color-bg-card)" }}>
          {draft.status.toUpperCase()}
        </span>
        {draft.verdict && (
          <span className="pill" style={{ color: VERDICT_COLOR[draft.verdict] ?? "var(--color-text-muted)", borderColor: "var(--color-border)", background: "var(--color-bg-card)" }}>
            ✓ {draft.verdict}
          </span>
        )}
        {voice?.tone && (
          <span className="font-mono text-[10px] text-[var(--color-text-dim)]">Voix : {voice.tone}</span>
        )}
        {draft.metrics && (
          <span className="font-mono text-[10px] text-[var(--color-text-muted)]">♥ {draft.metrics.likes ?? 0} · 💬 {draft.metrics.comments ?? 0}</span>
        )}
        <span className="ml-auto font-mono text-[10px] text-[var(--color-text-dim)] tabular-nums">{fmtTime(draft.created_at)}</span>
      </div>

      {editing ? (
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={8}
          className="mt-3 w-full p-3 rounded font-sans text-sm resize-y"
          style={{ background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
        />
      ) : (
        <div className="mt-3 rounded p-3" style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)" }}>
          <p className="text-sm text-[var(--color-text)] whitespace-pre-line leading-relaxed">{body}</p>
          {hashtags.length > 0 && (
            <p className="mt-2 font-mono text-[11px]" style={{ color: "var(--color-accent-bright)" }}>{hashtags.join(" ")}</p>
          )}
        </div>
      )}

      {showFeedback && (
        <div className="mt-3 rounded p-3" style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-status-error)" }}>
          <p className="font-mono text-[10px] uppercase tracking-wider" style={{ color: VERDICT_COLOR[draft.verdict!] ?? "var(--color-status-error)" }}>Verifier — {draft.verdict}</p>
          <ul className="mt-1 space-y-0.5 text-[12px] text-[var(--color-text-muted)]">
            {draft.verifier_feedback!.map((f, i) => <li key={i}>• {f}</li>)}
          </ul>
        </div>
      )}

      {draft.citations && draft.citations.length > 0 && (
        <details className="mt-3">
          <summary className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] cursor-pointer">
            Sources ({draft.citations.length})
          </summary>
          <ul className="mt-2 space-y-0.5 font-mono text-[11px] text-[var(--color-text-muted)]">
            {draft.citations.map((url) => (
              <li key={url}><a href={url} target="_blank" rel="noreferrer" className="hover:text-[var(--color-accent-bright)]">{url}</a></li>
            ))}
          </ul>
        </details>
      )}

      <div className="mt-4 pt-3 border-t border-[var(--color-border)]/60 flex flex-wrap gap-2 items-center">
        <span className="font-mono text-[10px] text-[var(--color-text-dim)] truncate max-w-md">
          {draft.content.length} chars · {draft.trend_source ?? "—"}
        </span>
        <span className="ml-auto" />
        {editing ? (
          <>
            <button
              onClick={() => { setEditing(false); onPatch({ content }); }}
              disabled={busy}
              className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider border"
              style={{ color: "var(--color-accent-glow)", borderColor: "var(--color-accent)", background: "var(--color-accent-soft)" }}
            >
              Save
            </button>
            <button
              onClick={() => { setEditing(false); setContent(draft.content); }}
              className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setEditing(true)}
              disabled={busy || draft.status === "published"}
              className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider border disabled:opacity-30"
              style={{ color: "var(--color-text)", borderColor: "var(--color-border)", background: "var(--color-bg-card)" }}
            >
              Edit
            </button>
            {draft.status === "draft" && (
              <button
                onClick={() => onPatch({ status: "approved" })}
                disabled={busy}
                className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider border disabled:opacity-30"
                style={{ color: "var(--color-status-running)", borderColor: "var(--color-border)", background: "var(--color-bg-card)" }}
              >
                Approve
              </button>
            )}
          </>
        )}
        <button
          onClick={handlePublish}
          disabled={!canPublish}
          className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider border disabled:opacity-30"
          style={{ color: "var(--color-status-active)", borderColor: "var(--color-status-active)", background: "var(--color-accent-soft)" }}
        >
          {autoMode ? "Publish-now" : "Publish"}
        </button>
        <button
          onClick={() => onPatch({ status: "rejected" })}
          disabled={busy || draft.status === "rejected" || draft.status === "published"}
          className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider border disabled:opacity-30"
          style={{ color: "var(--color-status-error)", borderColor: "var(--color-status-error)", background: "var(--color-bg-card)" }}
        >
          Reject
        </button>
      </div>
    </div>
  );
}
